import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Medal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchTopSites } from '../store/actions/siteActions';
import SeoHead from '../components/SeoHead';
import { sitePathSlug } from '../utils/sitePathSlug';
import AdBanner from '../components/ads/AdBanner';
import AdInline from '../components/ads/AdInline';
import { bannerAds, cardAds } from '../data/adData';
import styled from 'styled-components';

// Rank by rating and other factors (not traffic): user score first, then trending weight, then similarity
const trendingWeight = (t) => (t === 'Top choice' ? 3 : t === 'Rising' ? 2 : 1);

const Header = styled(motion.div)`
  margin-bottom: 2rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(234, 179, 8, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gold};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
`;

const RankNote = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
  font-style: italic;
`;

const PodiumGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PodiumCard = styled(Link)`
  display: block;
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  transition: box-shadow 0.2s;
  background: ${({ $bg, theme }) => ($bg ? $bg : theme.colors.card)};
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const PodiumTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const MedalIcon = styled(Medal)`
  color: ${({ $color }) => $color};
`;

const RankNum = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  font-weight: 800;
  color: ${({ $color }) => $color};
`;

const LogoBox = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.xl};
  margin-bottom: 0.75rem;
`;

const SiteTitle = styled.h3`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.lg};
  margin: 0;
`;

const SiteDomain = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
`;

const PodiumMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
`;

const ScorePill = styled.span`
  padding: 0.25rem 0.75rem;
  background: rgba(220, 38, 38, 0.1);
  color: ${({ theme }) => theme.colors.crimson};
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

const TrendingPill = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  background: ${({ $rising, $top }) => ($top ? 'rgba(234,179,8,0.15)' : $rising ? 'rgba(34,197,94,0.15)' : 'rgba(0,0,0,0.06)')};
  color: ${({ $rising, $top, theme }) => ($top ? theme.colors.gold : $rising ? 'hsl(142,71%,45%)' : theme.colors.mutedForeground)};
`;

const UserScoreMeta = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  strong {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ListRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const ListRank = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  color: ${({ theme }) => theme.colors.mutedForeground};
  width: 2.5rem;
  text-align: right;
`;

const ListLogo = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`;

const ListInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ListTitle = styled.h3`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
  font-size: ${({ theme }) => theme.fontClamp.sm};
`;

const ListDomain = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ListDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListMeta = styled.div`
  display: none;
  align-items: center;
  gap: 1rem;
  @media (min-width: 768px) {
    display: flex;
  }
`;

const StarBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  color: ${({ theme }) => theme.colors.gold};
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

function getRankStyle(i) {
  if (i === 0) return { color: 'var(--gold)', bg: 'rgba(234,179,8,0.1)' };
  if (i === 1) return { color: 'var(--muted-foreground)', bg: 'var(--muted)' };
  if (i === 2) return { color: 'var(--maroon)', bg: 'rgba(128,0,0,0.1)' };
  return { color: 'var(--muted-foreground)', bg: 'transparent' };
}

function TopSites() {
  const dispatch = useDispatch();
  const { topSites, loading, error } = useSelector((state) => state.sites);

  useEffect(() => {
    dispatch(fetchTopSites({ limit: 50 }));
  }, [dispatch]);

  const sorted = useMemo(
    () =>
      [...topSites].sort((a, b) => {
        const scoreA = (a.userScore || 0) * 10 + trendingWeight(a.trending || '') + (a.similarityScore || 0) / 100;
        const scoreB = (b.userScore || 0) * 10 + trendingWeight(b.trending || '') + (b.similarityScore || 0) / 100;
        return scoreB - scoreA;
      }),
    [topSites]
  );

  return (
    <PageLayout>
      <SeoHead
        title="Top Sites | CiteMatch"
        description="Top sites ranked by community rating, similarity, and momentum."
      />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <Header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <HeaderRow>
            <IconBox>
              <Trophy size={20} strokeWidth={2.5} />
            </IconBox>
            <div>
              <Title>Top Sites</Title>
              <Subtitle>Ranked by community rating, similarity, and momentum — not by traffic</Subtitle>
              <RankNote>Our ranking uses user scores, trending signal, and how well sites match what people look for.</RankNote>
            </div>
          </HeaderRow>
        </Header>

        {error && <p style={{ color: 'var(--crimson)', marginBottom: '1rem' }}>{error}</p>}
        {loading && sorted.length === 0 && <p style={{ color: 'var(--muted-foreground)' }}>Loading top sites…</p>}

        <PodiumGrid>
          {sorted.slice(0, 3).map((site, i) => {
            const style = getRankStyle(i);
            return (
              <motion.div
                key={site.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <PodiumCard to={`/site/${sitePathSlug(site)}`} $bg={style.bg}>
                  <PodiumTop>
                    <MedalIcon size={20} $color={style.color} />
                    <RankNum $color={style.color}>#{i + 1}</RankNum>
                  </PodiumTop>
                  <LogoBox>{site.title.charAt(0)}</LogoBox>
                  <SiteTitle>{site.title}</SiteTitle>
                  <SiteDomain>{site.domain}</SiteDomain>
                  <PodiumMeta>
                    {site.trending && (
                      <TrendingPill $rising={site.trending === 'Rising'} $top={site.trending === 'Top choice'}>
                        {site.trending === 'Rising' && '↑ '}{site.trending}
                      </TrendingPill>
                    )}
                    {site.userScore != null && (
                      <UserScoreMeta><strong>{site.userScore}/5</strong></UserScoreMeta>
                    )}
                    <ScorePill>{site.similarityScore}%</ScorePill>
                  </PodiumMeta>
                </PodiumCard>
              </motion.div>
            );
          })}
        </PodiumGrid>

        <div style={{ margin: '1.5rem 0' }}>
          <AdBanner ad={bannerAds[1]} variant="inline" />
        </div>

        <List>
          {sorted.slice(3, 6).map((site, i) => (
            <motion.div
              key={site.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (i + 3) * 0.03 }}
            >
              <ListRow to={`/site/${sitePathSlug(site)}`}>
                <ListRank>{i + 4}</ListRank>
                <ListLogo>{site.title.charAt(0)}</ListLogo>
                <ListInfo>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ListTitle>{site.title}</ListTitle>
                    <ListDomain>{site.domain}</ListDomain>
                  </div>
                  <ListDesc>{site.description}</ListDesc>
                </ListInfo>
                <ListMeta>
                  {site.trending && (
                    <TrendingPill $rising={site.trending === 'Rising'} $top={site.trending === 'Top choice'}>
                      {site.trending === 'Rising' && '↑ '}{site.trending}
                    </TrendingPill>
                  )}
                  {site.userScore != null && (
                    <UserScoreMeta><strong>{site.userScore}/5</strong></UserScoreMeta>
                  )}
                  <StarBadge><Star size={12} fill="currentColor" /> {(site.similarityScore / 10).toFixed(1)}</StarBadge>
                </ListMeta>
              </ListRow>
            </motion.div>
          ))}
        </List>

        <div style={{ margin: '1rem 0' }}>
          <AdInline ad={cardAds[0]} />
        </div>

        <List>
          {sorted.slice(6).map((site, i) => (
            <motion.div
              key={site.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (i + 6) * 0.03 }}
            >
              <ListRow to={`/site/${sitePathSlug(site)}`}>
                <ListRank>{i + 7}</ListRank>
                <ListLogo>{site.title.charAt(0)}</ListLogo>
                <ListInfo>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ListTitle>{site.title}</ListTitle>
                    <ListDomain>{site.domain}</ListDomain>
                  </div>
                  <ListDesc>{site.description}</ListDesc>
                </ListInfo>
                <ListMeta>
                  {site.trending && (
                    <TrendingPill $rising={site.trending === 'Rising'} $top={site.trending === 'Top choice'}>
                      {site.trending === 'Rising' && '↑ '}{site.trending}
                    </TrendingPill>
                  )}
                  {site.userScore != null && (
                    <UserScoreMeta><strong>{site.userScore}/5</strong></UserScoreMeta>
                  )}
                  <StarBadge><Star size={12} fill="currentColor" /> {(site.similarityScore / 10).toFixed(1)}</StarBadge>
                </ListMeta>
              </ListRow>
            </motion.div>
          ))}
        </List>
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default TopSites;
